import React, { useState, useEffect } from "react";
import LabelInput from "../../components/common/forms/Labelinput";
import Modal from "../../components/common/forms/modal";
import TextareaField from "../common/forms/textarea";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext"; // Auth context
import WarningModal from "../common/ui/warningmodel";

export default function AddCustomer({ isOpen, onClose, editData, onCustomerAdded }) {
  const [isProcessingService, setIsProcessingService] = useState(false);
  const [formData, setFormData] = useState({
    customerName: "",
    customerAddress: "",
  });
  const [errors, setErrors] = useState({});
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    if (editData) {
      setFormData({
        customerName: editData.customerName,
        customerAddress: editData.customerAddress,
      });
    } else {
      setFormData({ customerName: "", customerAddress: "" });
    }
  }, [editData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessingService(true);
    setErrors({});

    try {
      const payload = {
        name: formData.customerName,
        address: formData.customerAddress,
      };

      let response, result;

      // Helper function to call the correct API
      const callApi = async () => {
        if (editData) {
          // 🟡 Edit Customer API
          return await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.UPDATE_CUSTOMER(editData.id)}`,
            {
              method: "PUT",
              headers: getHeaders(true),
              body: JSON.stringify(payload),
            }
          );
        } else {
          // 🟢 Add Customer API
          return await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.CREATE}`,
            {
              method: "POST",
              headers: getHeaders(true),
              body: JSON.stringify(payload),
            }
          );
        }
      };

      // Initial API call
      response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call (POST or PUT) with new token
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      result = await response.json();

      if (response.ok && (result.status === 200 || result.status === 201)) {
        toast.success(
          result.message ||
          (editData ? "Customer updated successfully!" : "Customer added successfully!")
        );

        // Reset form and close modal
        setFormData({
          customerName: "",
          customerAddress: "",
        });
        setErrors({});

        if (onCustomerAdded) await onCustomerAdded(); // Refresh parent list
        onClose();
      } else {
        setWarningMessage(result.message || "Failed to save customer");
        setWarningVisible(true);
        setErrors({ api: result.message || "Failed to save customer" });
      }
    } catch (error) {
      console.error("Error adding/updating customer:", error);
      setWarningMessage("Something went wrong");
      setWarningVisible(true);
      setErrors({ api: "Something went wrong" });
    } finally {
      setIsProcessingService(false);
    }
  };



  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={editData ? "Edit Customer" : "Add New Customer"}
        onClose={onClose}
        onSubmit={handleSubmit}
        isProcessing={isProcessingService}
        submitText={editData ? "Update Customer" : "Add Customer"}
      >
        {/* Customer Name Input */}
        <LabelInput
          label="Customer Name"
          type="text"
          name="customerName"
          value={formData.customerName}
          onChange={handleChange}
          placeholder="Enter Customer Name"
          required
          error={errors.customerName}
        />

        {/* ✅ Customer Address Textarea */}
        <TextareaField
          label="Customer Address"
          name="customerAddress"
          value={formData.customerAddress}
          onChange={handleChange}
          placeholder="Enter Customer Address"
          required
          error={errors.customerAddress}
        />
      </Modal>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}
    </>
  );
}
